import { h } from 'preact';
import Store from 'store/store';
import * as jsonPointer from 'utils/jsonPointer';
import { confirm } from 'utils/modal';
import { canUser } from 'utils/roles';
import { joinClasses, sortObjectByOrder, sortObjectToList } from 'utils/utils';

import Button from 'components/button/button';
import { CardList, CardListItem } from 'components/cardList/cardList';
import MovieEditor from 'components/editor/movies/movieEditor';
import SelectableSection from 'components/editor/selectableSection';
import FieldWrapper from 'components/editor/settings/fieldWrapper';
import MediaField from 'components/editor/settings/mediaField';
import RichTextField from 'components/editor/settings/richTextField';
import SimpleField from 'components/editor/settings/simpleField';
import ToggleField from 'components/editor/settings/toggleField';

import s from 'components/editor/movies/synopsis/synopsis.sss';

class CardListItemInput extends CardListItem {
	renderLabel() {
		const readonly = !canUser('write');
		const { placeholder, value, onInput } = this.props;

		return (
			<input
				type="text"
				class={s.cardInput}
				value={value}
				placeholder={placeholder}
				disabled={readonly}
				onInput={onInput}
			/>
		);
	}

	renderInheritIndicator() {
		const readonly = !canUser('write');
		const base = super.renderInheritIndicator();
		if (base) {
			base.attributes.hideReset = readonly;
			base.attributes.primary = true;
		}
		return base;
	}

	renderAction() {
		const readonly = !canUser('write');
		const { onDelete } = this.props;

		if (!onDelete) return null;

		return (
			<Button class={s.cardDelete} icon="delete" disabled={readonly} onClick={onDelete} text />
		);
	}
}

export default class SynopsisEditor extends MovieEditor {
	s = s;

	constructor(props) {
		super(props);
		this.addCreditItem = this.addCreditItem.bind(this);
		this.addSection = this.addSection.bind(this);
		this.onDeleteCreditItemClick = this.onDeleteCreditItemClick.bind(this);
		this.onDeleteSectionClick = this.onDeleteSectionClick.bind(this);
		this.onCreditItemNameChange = this.onCreditItemNameChange.bind(this);
		this.onCreditItemsSort = this.onCreditItemsSort.bind(this);
		this.onSectionsSort = this.onSectionsSort.bind(this);
		this.renderCreditSectionItem = this.renderCreditSectionItem.bind(this);
	}

	componentDidMount() {
		super.componentDidMount?.();

		this.migrateCreditsToListFormat();
		this.removeDeletedInheritedCredits();
		this.setDefaultOnesheetAlt();
	}

	// Migrate old-format credits to new list format on load
	migrateCreditsToListFormat() {
		const credits = this.movie?.pages?.synopsis?.credits;
		if (!credits) return;


		Object.keys(credits).forEach((key) => {
			const section = credits[key];

			// Migrate sectionTitle to label if label is missing
			if (section.sectionTitle && !section.label) {
				jsonPointer.set(Store.get().data, `${this.props.dataRoot}/content/pages/synopsis/credits/${key}/label`, section.sectionTitle);
			}

			// If section has 'value' instead of 'credits', convert it
			if (section.value !== undefined && section.credits === undefined) {
				const currentValue = section.value || '';
				const newCredits = {};

				if (currentValue.trim()) {
					// Split by comma or "and" to create multiple items
					const names = currentValue.split(/,|\sand\s/).map(n => n.trim()).filter(n => n);
					names.forEach((name, index) => {
						const id = crypto.randomUUID();
						newCredits[id] = {
							id,
							order: index,
							name: name,
						};
					});
				} else {
					// Empty section, add one placeholder
					const id = crypto.randomUUID();
					newCredits[id] = {
						id,
						order: 0,
						name: '',
					};
				}

				// Update the section structure
				jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/pages/synopsis/credits/${key}`, {
					order: section.order,
					label: section.label || section.sectionTitle,
					credits: newCredits,
				});
			}
		});
	}

	// Sections/credits deleted globally are flagged as null on the base movie, so drop them locally too
	removeDeletedInheritedCredits() {
		const creditsPath = `${this.props.dataRoot}/content/pages/synopsis/credits`;
		const inheritedCredits = jsonPointer.get(Store.get().data, `${this.props.dataRoot}/base_movie/content/pages/synopsis/credits`) || {};

		Object.entries(inheritedCredits).forEach(([sectionKey, section]) => {
			if (section === null) {
				jsonPointer.removeDeep(Store.get().data, `${creditsPath}/${sectionKey}`);
			} else if (section) {
				Object.entries(section.credits || {})
					.filter(([, credit]) => credit === null)
					.forEach(([creditKey]) => {
						jsonPointer.removeDeep(Store.get().data, `${creditsPath}/${sectionKey}/credits/${creditKey}`);
					});
			}
		});
	}

	// Set default onesheet alt text if onesheet exists but alt is missing
	setDefaultOnesheetAlt() {
		const onesheet = this.movie?.onesheet?.url;
		const existingAlt = this.movie?.onesheet?.alt;
		if (!onesheet || existingAlt) return;

		const movieTitle = this.movie?.title || '';
		jsonPointer.set(Store.get().data, `${this.props.dataRoot}/content/onesheet/alt`, `${movieTitle} Poster`);
	}

	// ===============================
	// HELPERS
	// ===============================

	getPlaceholderCredits() {
		return {
			cast: { label: 'STARRING', order: 1, credits: { new: { name: 'Add cast...', order: 1 } } },
			directors: { label: 'DIRECTED BY', order: 2, credits: { new: { name: 'Add director...', order: 1 } } },
			writers: { label: 'WRITTEN BY', order: 3, credits: { new: { name: 'Add writer...', order: 1 } } },
			producers: { label: 'PRODUCED BY', order: 4, credits: { new: { name: 'Add producer...', order: 1 } } },
		};
	}

	flattenCredits(credits = {}) {
		const entries = Object.entries(credits)
			.map(([key, value]) => ({ key, value }))
			.sort((a, b) => (a.value.order || 0) - (b.value.order || 0));

		// Default labels for each credit type 
		const defaultLabels = this.getPlaceholderCredits();

		const result = entries.flatMap(({ key, value }) => {
			// All sections use the same list structure
			const sectionCredits = sortObjectByOrder(value.credits, (val, id) => [id, val]);

			// All sections now use 'label' field consistently
			const sectionLabel = value.label || value.sectionTitle || defaultLabels[key]?.label || '';

			return [
				{
					id: key,
					label: sectionLabel,
					type: 'list-section',
					credits: sectionCredits.map(([id, member]) => ({
						id,
						name: member.name,
						order: member.order || 0,
						path: `${this.props.dataRoot}/content/pages/synopsis/credits/${key}/credits/${id}`,
					})),
				},
			];
		});

		return result.length ? result : null;
	}

	addSection() {
		const id = crypto.randomUUID();
		const creditsPath = `${this.props.dataRoot}/content/pages/synopsis/credits`;
		const credits = this.movie?.pages?.synopsis?.credits || {};

		// Find the highest order number and add 1
		const maxOrder = Math.max(0, ...Object.values(credits).map(c => c.order || 0));

		// Create new section with list structure (like Cast)
		jsonPointer.setDeep(Store.get().data, `${creditsPath}/${id}`, {
			order: maxOrder + 1,
			label: 'New Section',
			credits: {
				[crypto.randomUUID()]: { name: 'New Credit', order: 1 }
			},
		});
	}

	async onDeleteSectionClick(sectionKey) {
		const ok = await confirm('Are you sure you want to delete this section?', {
			title: 'Delete section?',
			danger: true,
			button: 'Delete',
		});
		if (!ok) return;

		const { dataRoot } = this.props;
		const path = `/content/pages/synopsis/credits/${sectionKey}`;
		const isInherited = jsonPointer.has(Store.get().data, `${dataRoot}/base_movie${path}`);
		if (isInherited) {
			jsonPointer.setDeep(Store.get().data, `${dataRoot}${path}`, null);
		} else {
			jsonPointer.removeDeep(Store.get().data, `${dataRoot}${path}`);
		}
		this.props.select('/pages/synopsis/credits');
	}

	onSectionsSort(e) {
		const allSections = [...e.target.querySelectorAll('[data-section]')].map((el) => el.dataset.section);
		allSections.splice(e.to, 0, allSections.splice(e.from, 1)[0]);
		allSections.forEach((key, index) => {
			jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/pages/synopsis/credits/${key}/order`, index + 1);
		});
	}

	onCreditItemsSort(e, creditKey) {
		const allItems = [...e.target.querySelectorAll('[data-section]')].map((el) => el.dataset.section);
		allItems.splice(e.to, 0, allItems.splice(e.from, 1)[0]);

		allItems.forEach((id, index) => {
			jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/pages/synopsis/credits/${creditKey}/credits/${id}/order`, index + 1);
		});
	}

	addCreditItem(creditKey) {
		const id = crypto.randomUUID();
		const path = `${this.props.dataRoot}/content/pages/synopsis/credits/${creditKey}/credits`;
		const credits = this.movie?.pages?.synopsis?.credits?.[creditKey]?.credits || {};

		// Find the highest order number and add 1
		const maxOrder = Math.max(0, ...Object.values(credits).map(c => c.order || 0));

		jsonPointer.setDeep(Store.get().data, `${path}/${id}`, {
			order: maxOrder + 1,
			name: 'New Credit',
		});
	}

	async onDeleteCreditItemClick(path, inheritPath) {
		const ok = await confirm('Are you sure you want to delete this credit?', {
			title: 'Delete credit?',
			danger: true,
			button: 'Delete',
		});
		if (!ok) return;

		const isInherited = jsonPointer.has(Store.get().data, inheritPath);
		if (isInherited) {
			jsonPointer.setDeep(Store.get().data, path, null);
		} else {
			jsonPointer.removeDeep(Store.get().data, path);
		}
	}

	async onCreditItemNameChange(e, path) {
		const namePath = `${path}/name`;
		const name = e.target.value;
		if (name) {
			jsonPointer.setDeep(Store.get().data, namePath, name);
		} else {
			// If the name is empty, remove it from the data
			jsonPointer.removeDeep(Store.get().data, namePath);
		}
	}

	// ===============================
	// VISUAL
	// ===============================

	renderOnesheet() {
		const synopsisOnesheet = this.movie?.pages?.synopsis?.onesheet || {};
		const onesheetData = synopsisOnesheet?.custom ? synopsisOnesheet : this.movie?.onesheet;

		const onesheet = onesheetData?.url;
		const onesheetAlt = onesheetData?.alt || `${this.movie?.title || 'Movie Title'} Poster`;

		return (
			<div class={this.s.onesheetWrapper}>
				<SelectableSection {...this.props} section="/pages/synopsis/onesheet" class={this.s.onesheet}>
					{onesheet ? (
						<img src={onesheet} class={this.s.image} alt={onesheetAlt} />
					) : (
						<div class={this.s.emptyOnesheet} style={{ cursor: 'pointer' }}>
							<span class={this.s.addLabel}>+ Add onesheet</span>
						</div>
					)}
				</SelectableSection>
			</div>
		);
	}

	renderSynopsis() {
		const synopsisText = this.movie?.pages?.synopsis?.synopsis || '';
		// Strip HTML tags and check if there's actual text content
		const textOnly = synopsisText.replace(/<[^>]*>/g, '').trim();
		const hasSynopsisText = textOnly.length > 0;

		return (
			<SelectableSection {...this.props} section="/pages/synopsis/text" class={joinClasses(this.s.synopsisText, !hasSynopsisText && this.s.empty)}>
				<div dangerouslySetInnerHTML={{ __html: hasSynopsisText ? synopsisText : 'Add synopsis...' }} />
			</SelectableSection>
		);
	}

	/**
	 * Renders the list of credits for a credit section (e.g. Cast, Directors, etc) in the synopsis page
	 */
	renderCreditList(section) {
		const hasCredits = section?.credits && Object.keys(section.credits).length > 0;
		const credits = hasCredits ? section.credits : [{ id: 0, name: 'Add credits...' }];
		
		return credits.map((member) => (
			<div class={joinClasses(this.s.credit, !hasCredits && this.s.empty)} key={member.id}>
				<span>{member.name}</span>
			</div>
		));
	}

	renderCredits() {
		const content = this.movie?.pages?.synopsis || {};
		const credits = this.flattenCredits(content.credits || {});
		const placeholder = this.flattenCredits(this.getPlaceholderCredits());

		return (
			<SelectableSection {...this.props} section="/pages/synopsis/credits" class={joinClasses(this.s.credits, !credits && this.s.empty)}>
				{(credits || placeholder).map((section) => (
					<div class={this.s.creditSection} key={section.id}>
						<div class={this.s.creditSectionTitle}>{section.label}</div>
						{this.renderCreditList(section)}
					</div>
				))}
			</SelectableSection>
		);
	}

	renderVisual() {
		return (
			<SelectableSection class={this.s.wrapper} section="/pages/synopsis" {...this.props}>
				<div class={this.s.columns}>
					{/* Onesheet */}
					{this.renderOnesheet()}

					{/* Synopsis */}
					{this.renderSynopsis()}

					{/* Credits */}
					{this.renderCredits()}
				</div>
			</SelectableSection>
		);
	}

	// ===============================
	// SETTINGS SIDEBAR
	// ===============================

	renderSettingsOverview() {
		return (
			<div class={this.s.list}>
				<CardListItem label="Onesheet" onEdit={() => this.props.select(`/pages/synopsis/onesheet`)} />
				<CardListItem label="Synopsis" onEdit={() => this.props.select(`/pages/synopsis/text`)} />
				<CardListItem label="Credits" onEdit={() => this.props.select(`/pages/synopsis/credits`)} />
			</div>
		);
	}

	renderOnesheetSettings() {
		const readonly = !canUser('write');
		const { data, dataRoot } = this.props;
		const { id, content, title } = data || {};
		
		const useCustom = content?.pages?.synopsis?.onesheet?.custom;
		const root = `${dataRoot}/content${useCustom ? '/pages/synopsis' : ''}/onesheet`;

		return (
			<div class={this.s.onesheetSettings}>
				<ToggleField dark path={`${dataRoot}/content/pages/synopsis/onesheet/custom`}>
					Use unique movie page onesheet
				</ToggleField>
				<MediaField
					dark
					label="Onesheet"
					path={`${root}/url`}
					group={id}
					assetData={{ type: 'Onesheet', aspectRatio: '350:517', recommendedDimensions: '1400:2068' }}
					disabled={readonly || !useCustom}
				/>
				<SimpleField
					dark
					label="Alt text"
					placeholder={`${title || 'Movie Title'} Poster`}
					path={`${root}/alt`}
					disabled={readonly || !useCustom}
				/>
			</div>
		);
	}

	renderSynopsisTextSettings() {
		const formats = ['bold', 'italic', 'underline', 'strike', 'link', 'align'];

		return (
			<div class={this.s.synopsisTextEditor}>
				<RichTextField
					label="Synopsis"
					path={`${this.props.dataRoot}/content/pages/synopsis/synopsis`}
					formats={formats}
					rows={24}
				/>
			</div>
		);
	}

	renderCreditsOverviewSettings(credits) {
		const readonly = !canUser('write');
		const sections = sortObjectToList(credits);

		return (
			<FieldWrapper
				label="Sections"
				path={`${this.props.dataRoot}/content/pages/synopsis/credits`}
				inheritPath={`${this.props.dataRoot}/base_movie/content/pages/synopsis/credits`}
				inheritGroup
				dark
			>
				<CardList
					items={sections}
					getItemProps={(item) => ({ 
						'data-section': item.id,
						label: item.label || item.sectionTitle || item.id.toUpperCase(),
						path: `${this.props.dataRoot}/content/pages/synopsis/credits/${item.id}`,
						inheritPath: `${this.props.dataRoot}/base_movie/content/pages/synopsis/credits/${item.id}`,
						inheritGroup: true,
						onEdit: () => this.props.select(`/pages/synopsis/credits/${item.id}`),
					})}
					onChange={this.onSectionsSort}
					disabled={readonly}
				/>
				<Button dark primary icon="plus" onClick={this.addSection} disabled={readonly}>
					Add Section
				</Button>
			</FieldWrapper>
		);
	}

	renderCreditSectionItem(item, index, props) {
		const { path, inheritPath } = props;
		const data = jsonPointer.get(Store.get().data, path);
		const inheritValue = jsonPointer.get(Store.get().data, inheritPath);

		return (
			<CardListItemInput
				{...props}
				value={data?.name}
				placeholder={inheritValue?.name || 'Name'}
				onInput={(e) => this.onCreditItemNameChange(e, path)}
				onDelete={() => this.onDeleteCreditItemClick(path, inheritPath)}
			/>
		);
	}

	renderCreditSectionSettings(creditKey, credits) {
		const { dataRoot } = this.props;
		const readonly = !canUser('write');
		const sectionPath = `/content/pages/synopsis/credits/${creditKey}`;
		const sectionCredits = sortObjectToList(credits[creditKey]?.credits);

		return (
			<div class={this.s.section}>
				<SimpleField
					dark
					label="Title"
					placeholder="e.g. Cast"
					path={`${dataRoot}${sectionPath}/label`}
					inheritPath={`${dataRoot}/base_movie${sectionPath}/label`}
				/>
				<FieldWrapper
					label="Credits"
					path={`${dataRoot}${sectionPath}/credits`}
					inheritPath={`${dataRoot}/base_movie${sectionPath}/credits`}
					inheritGroup
					dark
				>
					<CardList
						items={sectionCredits}
						getItemProps={({ id }) => ({
							'data-section': id,
							path: `${dataRoot}${sectionPath}/credits/${id}`,
							inheritPath: `${dataRoot}/base_movie${sectionPath}/credits/${id}`,
						})}
						renderItem={this.renderCreditSectionItem}
						onChange={(e) => this.onCreditItemsSort(e, creditKey)}
					/>
					<Button dark primary icon="plus" onClick={() => this.addCreditItem(creditKey)} disabled={readonly}>
						Add credit
					</Button>
				</FieldWrapper>
				<Button dark text danger class={this.s.deleteSectionButton} onClick={() => this.onDeleteSectionClick(creditKey)}>
					Delete section
				</Button>
			</div>
		);
	}

	renderSettings() {
		const activeElement = this.props.activeElement;
		const nav = [{ label: 'Synopsis', path: '/pages/synopsis' }];
		let sectionContent = null;

		if (activeElement === '/pages/synopsis') {
			sectionContent = this.renderSettingsOverview();
		} else if (activeElement === '/pages/synopsis/onesheet') {
			nav.push({ label: 'Onesheet', path: '/pages/synopsis/onesheet' });
			sectionContent = this.renderOnesheetSettings();
		} else if (activeElement === '/pages/synopsis/text') {
			nav.push({ label: 'Synopsis', path: '/pages/synopsis/text' });
			sectionContent = this.renderSynopsisTextSettings();
		} else if (activeElement.startsWith('/pages/synopsis/credits')) {
			nav.push({ label: 'Credits', path: '/pages/synopsis/credits' });
			const credits = this.movie?.pages?.synopsis?.credits || {};

			if (activeElement === '/pages/synopsis/credits') {
				sectionContent = this.renderCreditsOverviewSettings(credits);
			} else {
				const [, , , , creditKey] = activeElement.split('/');
				nav.push({ label: 'Section', path: `/pages/synopsis/credits/${creditKey}` });
				sectionContent = this.renderCreditSectionSettings(creditKey, credits);
			}
		}

		return (
			<div class={this.s.settings}>
				{nav && this.renderSettingsNavigation(nav)}
				{sectionContent}
			</div>
		);
	}
}
